import { useState, useEffect } from "react";
import { aGet, aPost, aPut, aDelete } from "../../utils/adminApi";
import { C, Input, Select, Btn, Table, Modal } from "./AdminPanel";

// React Icons imports
import { FiPlus, FiEdit2, FiTrash2, FiTag, FiPercent, FiDollarSign, FiCalendar, FiUsers, FiCheck, FiX, FiInbox } from "react-icons/fi";
import { FaRupeeSign, FaTicketAlt, FaGift, FaStopwatch, FaInfinity, FaToggleOn, FaToggleOff } from "react-icons/fa";
import { MdLocalOffer, MdDiscount, MdMoneyOff, MdTimer, MdPeople, MdCheckCircle, MdCancel } from "react-icons/md";

const emptyCoupon = { code: "", discountType: "percentage", discountValue: "", minOrderValue: 0, maxDiscount: "", usageLimit: "", isActive: true, expiresAt: "" };

export default function Coupons({ toast }) {
    const [coupons, setCoupons] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modal, setModal] = useState(null);
    const [form, setForm] = useState(emptyCoupon);
    const [saving, setSaving] = useState(false);

    const load = () => {
        setLoading(true);
        aGet("/coupons").then(setCoupons).catch(e => toast(e.message, "error")).finally(() => setLoading(false));
    };
    useEffect(load, [toast]);

    const openAdd = () => { setForm(emptyCoupon); setModal("add"); };
    const openEdit = (c) => { setForm({ ...c, expiresAt: c.expiresAt ? c.expiresAt.split("T")[0] : "" }); setModal("edit"); };

    const handleSave = async () => {
        if (!form.code || !form.discountValue) return toast("Code and discount value required", "error");
        setSaving(true);
        try {
            if (modal === "add") await aPost("/coupons", form);
            else await aPut(`/coupons/${form.id}`, form);
            toast(modal === "add" ? "Coupon created ✓" : "Coupon updated ✓");
            setModal(null);
            load();
        } catch (e) { toast(e.message, "error"); }
        finally { setSaving(false); }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Delete this coupon?")) return;
        try { await aDelete(`/coupons/${id}`); toast("Coupon deleted"); load(); }
        catch (e) { toast(e.message, "error"); }
    };

    const toggleActive = async (c) => {
        try { await aPut(`/coupons/${c.id}`, { ...c, isActive: !c.isActive }); load(); }
        catch (e) { toast(e.message, "error"); }
    };

    return (
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <Btn onClick={openAdd}>
                    <FiPlus /> New Coupon
                </Btn>
            </div>

            <div style={{ border: `1px solid ${C.border}`, borderRadius: 14, overflow: "hidden" }}>
                <Table
                    loading={loading}
                    columns={[
                        {
                            key: "code",
                            label: "Code",
                            render: v => <span style={{ fontFamily: "monospace", color: C.primary, fontWeight: 800, fontSize: 14 }}>
                                <FaTicketAlt /> {v}
                            </span>
                        },
                        {
                            key: "discountType",
                            label: "Type",
                            render: v => v === "percentage" ? <><FiPercent /> %</> : <><FaRupeeSign /> Fixed</>
                        },
                        {
                            key: "discountValue",
                            label: "Value",
                            render: (v, row) => row.discountType === "percentage" ?
                                <><FiPercent /> {v}%</> :
                                <><FaRupeeSign /> {v}</>
                        },
                        {
                            key: "minOrderValue",
                            label: "Min Order",
                            render: v => <><FaRupeeSign /> {v}</>
                        },
                        {
                            key: "usageLimit",
                            label: "Limit",
                            render: (v, row) => v ?
                                <><FiUsers /> {row.usedCount}/{v}</> :
                                <><FaInfinity /> {row.usedCount}/∞</>
                        },
                        {
                            key: "expiresAt",
                            label: "Expires",
                            render: v => v ?
                                <><FiCalendar /> {new Date(v).toLocaleDateString("en-IN")}</> :
                                <><FaInfinity /> Never</>
                        },
                        {
                            key: "isActive",
                            label: "Active",
                            render: (v, row) => (
                                <button
                                    onClick={() => toggleActive(row)}
                                    style={{
                                        background: v ? C.green : C.red,
                                        border: "none",
                                        color: "#fff",
                                        padding: "3px 10px",
                                        borderRadius: 20,
                                        fontSize: 11,
                                        fontWeight: 700,
                                        cursor: "pointer",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "4px"
                                    }}
                                >
                                    {v ? <FiCheck /> : <FiX />}
                                    {v ? "Active" : "Inactive"}
                                </button>
                            )
                        },
                        {
                            key: "id",
                            label: "Actions",
                            render: (v, row) => (
                                <div style={{ display: "flex", gap: 6 }}>
                                    <Btn size="sm" variant="ghost" onClick={() => openEdit(row)}>
                                        <FiEdit2 />
                                    </Btn>
                                    <Btn size="sm" variant="red" onClick={() => handleDelete(v)}>
                                        <FiTrash2 />
                                    </Btn>
                                </div>
                            ),
                        },
                    ]}
                    data={coupons}
                />
            </div>

            {modal && (
                <Modal title={modal === "add" ? "Create Coupon" : "Edit Coupon"} onClose={() => setModal(null)}>
                    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                            <Input
                                label="Coupon Code *"
                                value={form.code}
                                onChange={e => setForm(p => ({ ...p, code: e.target.value.toUpperCase() }))}
                                placeholder="e.g. SAVE20"
                                icon={<MdLocalOffer />}
                            />
                            <Select
                                label="Discount Type"
                                value={form.discountType}
                                onChange={e => setForm(p => ({ ...p, discountType: e.target.value }))}
                            >
                                <option value="percentage"><FiPercent /> Percentage (%)</option>
                                <option value="fixed"><FaRupeeSign /> Fixed Amount (₹)</option>
                            </Select>
                            <Input
                                label={`Discount Value (${form.discountType === "percentage" ? "%" : "₹"}) *`}
                                type="number"
                                value={form.discountValue}
                                onChange={e => setForm(p => ({ ...p, discountValue: Number(e.target.value) }))}
                                icon={form.discountType === "percentage" ? <FiPercent /> : <FaRupeeSign />}
                            />
                            <Input
                                label="Min Order Value (₹)"
                                type="number"
                                value={form.minOrderValue}
                                onChange={e => setForm(p => ({ ...p, minOrderValue: Number(e.target.value) }))}
                                icon={<FaRupeeSign />}
                            />
                            <Input
                                label="Max Discount (₹)"
                                type="number"
                                placeholder="Leave blank for no limit"
                                value={form.maxDiscount}
                                onChange={e => setForm(p => ({ ...p, maxDiscount: e.target.value ? Number(e.target.value) : "" }))}
                                icon={<MdMoneyOff />}
                            />
                            <Input
                                label="Usage Limit"
                                type="number"
                                placeholder="Leave blank for unlimited"
                                value={form.usageLimit}
                                onChange={e => setForm(p => ({ ...p, usageLimit: e.target.value ? Number(e.target.value) : "" }))}
                                icon={<FiUsers />}
                            />
                            <Input
                                label="Expires At"
                                type="date"
                                value={form.expiresAt}
                                onChange={e => setForm(p => ({ ...p, expiresAt: e.target.value }))}
                                icon={<FiCalendar />}
                            />
                            <div style={{ display: "flex", flexDirection: "column", justifyContent: "flex-end" }}>
                                <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 13, color: C.muted }}>
                                    <input type="checkbox" checked={form.isActive} onChange={e => setForm(p => ({ ...p, isActive: e.target.checked }))} />
                                    {form.isActive ? <FiCheck style={{ color: C.green }} /> : <FiX style={{ color: C.red }} />}
                                    Active
                                </label>
                            </div>
                        </div>
                        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
                            <Btn variant="ghost" onClick={() => setModal(null)}>Cancel</Btn>
                            <Btn onClick={handleSave} disabled={saving}>
                                {saving ? "Saving…" : modal === "add" ? "Create Coupon" : "Update Coupon"}
                            </Btn>
                        </div>
                    </div>
                </Modal>
            )}
        </div>
    );
}